/**
 * Session state for the three surfaces.
 *
 * The backend has three separate session kinds — member, admin, and no session
 * for public — and they are deliberately not interchangeable. So the frontend
 * keeps three independent records rather than one "isLoggedIn" flag, because
 * that is what the server actually enforces: a member cookie is refused by the
 * admin API, and a staff account is refused by the member API.
 *
 * CSRF is held in memory only. It comes back from the login response body and is
 * never persisted to storage, so a stale token cannot survive a reload with a
 * dead session.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  adoptCsrfToken,
  api,
  ApiError,
  type AdminSession,
  type MemberSession,
} from "./api.ts";

interface SessionState {
  member: MemberSession | null;
  admin: AdminSession | null;
  memberChecked: boolean;
  adminChecked: boolean;
  /** Why the member session is missing, so the guard can explain it. */
  memberError: ApiError | null;
  signInMember(username: string, password: string): Promise<void>;
  signInAdmin(username: string, password: string): Promise<void>;
  signOutMember(): Promise<void>;
  signOutAdmin(): Promise<void>;
  refreshMember(): Promise<void>;
  refreshAdmin(): Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

interface CredentialsResponse {
  csrf: string;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [member, setMember] = useState<MemberSession | null>(null);
  const [admin, setAdmin] = useState<AdminSession | null>(null);
  const [memberChecked, setMemberChecked] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [memberError, setMemberError] = useState<ApiError | null>(null);

  // Public pages never ask about a session: most visitors have none, and the
  // check would be a wasted round trip on the surface that must feel fastest.
  const checkMember = useCallback(async () => {
    try {
      const session = await api.get<MemberSession>("/api/member/session");
      setMember(session);
      setMemberError(null);
    } catch (caught) {
      // Two different failures land here and only one of them should read as
      // "signed out": a missing session, versus a session that is valid but
      // belongs to an account with no member profile. Collapsing both into a
      // silent redirect is what made a staff account look like a failed login —
      // credentials were correct and nothing was ever printed.
      setMember(null);
      setMemberError(caught instanceof ApiError ? caught : null);
    } finally {
      setMemberChecked(true);
    }
  }, []);

  const checkAdmin = useCallback(async () => {
    try {
      const session = await api.get<AdminSession>("/api/admin/session");
      setAdmin(session);
    } catch {
      setAdmin(null);
    } finally {
      setAdminChecked(true);
    }
  }, []);

  const signInMember = useCallback(async (username: string, password: string) => {
    const result = await api.post<CredentialsResponse>("/api/member/login", { username, password }, {
      skipCsrf: true,
    });
    adoptCsrfToken(result.csrf);
    await checkMember();
  }, [checkMember]);

  const signInAdmin = useCallback(async (username: string, password: string) => {
    const result = await api.post<CredentialsResponse>("/api/admin/login", { username, password }, {
      skipCsrf: true,
    });
    adoptCsrfToken(result.csrf);
    await checkAdmin();
  }, [checkAdmin]);

  const signOutMember = useCallback(async () => {
    try {
      await api.post("/api/member/logout");
    } catch (error) {
      // A session that is already gone is the desired end state; only surface
      // genuine failures.
      if (!(error instanceof ApiError) || !error.isUnauthenticated) throw error;
    }
    setMember(null);
    setMemberError(null);
  }, []);

  const signOutAdmin = useCallback(async () => {
    try {
      await api.post("/api/admin/logout");
    } catch (error) {
      if (!(error instanceof ApiError) || !error.isUnauthenticated) throw error;
    }
    setAdmin(null);
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      member,
      admin,
      memberChecked,
      adminChecked,
      memberError,
      signInMember,
      signInAdmin,
      signOutMember,
      signOutAdmin,
      refreshMember: checkMember,
      refreshAdmin: checkAdmin,
    }),
    [member, admin, memberChecked, adminChecked, memberError, signInMember, signInAdmin, signOutMember, signOutAdmin, checkMember, checkAdmin]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside SessionProvider");
  return context;
}
